// Advanced Intelligent Search Utility for Urdu and English (Pansar & Tibb-e-Pakistani)
import { CATEGORIES, AILMENTS_LIST, MIZAJ_LIST } from '../data/categoriesData';

// Common Pansar & Tibbi Synonym Equivalents
const HERB_SYNONYMS = [
  ['اسپغول', 'اسبغول', 'اسبغو', 'چھلکا اسپغول', 'تخم اسبغول', 'ispaghol', 'isabgol', 'psyllium', 'husk'],
  ['موصلی', 'موسلی', 'موصلی سفید', 'موسلی سفید', 'موصلی سیاہ', 'موصلی انڈین', 'موصلی انڈیا', 'safed musli', 'musli'],
  ['ستاور', 'ستااور', 'شتاور', 'شتاوری', 'shatavari', 'asparagus', 'satavar'],
  ['کلونجی', 'سیاہ دانہ', 'سیاہدانہ', 'حبتہ البرکت', 'حبة البركة', 'kalonji', 'black seed', 'nigella'],
  ['تخم ملنگا', 'تخم بالنگا', 'تخم ملنگاں', 'تخم ریحان', 'tukhm malanga', 'basil seeds', 'chia'],
  ['شہد', 'عسل', 'چھوٹی مکھی', 'بڑی مکھی', 'honey', 'pure honey', 'asal'],
  ['سنا مکی', 'سنامکی', 'برگ سنا', 'سنا', 'senna', 'senna leaves'],
  ['گوند کتیرا', 'گوندکتیرا', 'گوند', 'کتیرا', 'gond katira', 'tragacanth'],
  ['ملیٹھی', 'ملٹھی', 'اصل السوس', 'mulethi', 'licorice', 'liquorice'],
  ['دارچینی', 'دار چینی', 'قرفہ', 'cinnamon'],
  ['سونف', 'بادیان', 'رازیانہ', 'saunf', 'fennel'],
  ['زیرہ', 'زیرہ سفید', 'زیرہ سیاہ', 'کمون', 'zeera', 'zira', 'cumin'],
  ['اجوائن', 'اجوائن دیسی', 'اجواین', 'نانخواہ', 'ajwain', 'carom'],
  ['الائچی', 'چھوٹی الائچی', 'بڑی الائچی', 'ہیل', 'cardamom', 'elachi'],
  ['زعفران', 'کیسر', 'zafran', 'saffron'],
  ['عرق گلاب', 'گلاب', 'rose water', 'arq gulab'],
  ['جوارش کمونی', 'کمونی', 'jawarish kamuni'],
  ['معجون فلاسفہ', 'فلاسفہ', 'majun falasfa'],
  ['خمیرہ مروارید', 'مروارید', 'khamira marwareed'],
  ['عرق مکو', 'مکو', 'arq mako'],
  ['عرق کاسنی', 'کاسنی', 'arq kasni'],
  ['روغن بادام', 'بادام کا تیل', 'almond oil', 'roghan badam'],
  ['روغن زیتون', 'زیتون', 'olive oil', 'roghan zaitoon']
];

/**
 * Standard Unicode text normalizer (Removes aerab, unifies standard Urdu characters)
 */
export const normalizeSearchText = (text) => {
  if (!text || typeof text !== 'string') return '';

  let str = text.toLowerCase().trim();

  // Remove Arabic / Urdu Diacritics (Aerab / Tashkeel)
  str = str.replace(/[\u064B-\u065F\u0670\u06D6-\u06ED]/g, '');

  // Normalize Alif variants (آ, أ, إ, ٱ -> ا)
  str = str.replace(/[آأإٱ]/g, 'ا');

  // Normalize Yeh variants (ي, ى, ے, ئ -> ی)
  str = str.replace(/[يىےئ]/g, 'ی');

  // Normalize Kaf (Arabic ك -> Urdu ک)
  str = str.replace(/[ك]/g, 'ک');

  // Normalize He variants (ة, ه, ھ, ۂ -> ہ)
  str = str.replace(/[ةهھۂ]/g, 'ہ');

  // Normalize Waw variants (ؤ -> و)
  str = str.replace(/[ؤ]/g, 'و');

  // Normalize Nun Ghunna (ں -> ن)
  str = str.replace(/[ں]/g, 'ن');

  // Remove common punctuation and special chars
  str = str.replace(/[()_.,/\\+\-–—:;"'!?؟،]/g, ' ');

  // Collapse multiple spaces
  str = str.replace(/\s+/g, ' ').trim();

  return str;
};

/**
 * Phonetic / Homophone Normalizer for ultra-forgiving fuzzy Urdu matching
 * Maps letters that sound similar or are frequently mistyped to common phonetic representatives
 */
export const phoneticNormalize = (text) => {
  if (!text || typeof text !== 'string') return '';

  let str = normalizeSearchText(text);

  // Urdu sound-alike mappings:
  str = str.replace(/[صث]/g, 'س');       // ص, ث -> س (e.g. موصلی <-> موسلی, ثابت <-> سابت)
  str = str.replace(/[ضظذ]/g, 'ز');       // ض, ظ, ذ -> ز
  str = str.replace(/[ط]/g, 'ت');         // ط -> ت
  str = str.replace(/[حھ]/g, 'ہ');        // ح, ھ -> ہ
  str = str.replace(/[قگ]/g, 'ک');        // ق, گ -> ک
  str = str.replace(/[غ]/g, 'گ');         // غ -> گ
  str = str.replace(/[پ]/g, 'ب');         // پ -> ب (e.g. اسپغول <-> اسبغول)
  str = str.replace(/[ژ]/g, 'ز');         // ژ -> ز
  str = str.replace(/[ٹ]/g, 'ت');         // ٹ -> ت
  str = str.replace(/[ڈ]/g, 'د');         // ڈ -> د
  str = str.replace(/[ڑ]/g, 'ر');         // ڑ -> ر

  return str;
};

/**
 * Levenshtein distance for fuzzy typo tolerance
 */
export const getEditDistance = (a, b) => {
  if (!a || !b) return (a || b || '').length;
  const matrix = [];

  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1,     // insertion
          matrix[i - 1][j] + 1      // deletion
        );
      }
    }
  }

  return matrix[b.length][a.length];
};

/**
 * Generate all search keywords/synonyms for a query
 */
export const expandQuerySynonyms = (query) => {
  const normalized = normalizeSearchText(query);
  const phonetic = phoneticNormalize(query);
  if (!normalized) return [];

  const results = new Set([normalized, phonetic, query.toLowerCase().trim()]);

  // Check synonym groups
  HERB_SYNONYMS.forEach(group => {
    const normGroup = group.map(g => normalizeSearchText(g));
    const phonGroup = group.map(g => phoneticNormalize(g));

    const isMatch = normGroup.some(item => 
      item.includes(normalized) || normalized.includes(item)
    ) || phonGroup.some(item => 
      item.includes(phonetic) || phonetic.includes(item)
    );

    if (isMatch) {
      normGroup.forEach(item => results.add(item));
      phonGroup.forEach(item => results.add(item));
      group.forEach(item => results.add(item.toLowerCase()));
    }
  });

  return Array.from(results);
};

/**
 * Builds a searchable index string for a product
 */
export const buildProductSearchCorpus = (product) => {
  if (!product) return '';

  const parts = [];

  // Direct product names
  if (product.nameUrdu) parts.push(product.nameUrdu);
  if (product.nameEn) parts.push(product.nameEn);

  // Synonyms/Aliases if provided on product
  if (Array.isArray(product.aliases)) parts.push(...product.aliases);
  if (Array.isArray(product.tags)) parts.push(...product.tags);
  if (Array.isArray(product.keywords)) parts.push(...product.keywords);

  // Benefits & Usage
  if (product.benefitsUrdu) parts.push(product.benefitsUrdu);
  if (product.benefitsEn) parts.push(product.benefitsEn);
  if (product.usageUrdu) parts.push(product.usageUrdu);

  // Mizaj
  if (product.mizajUrdu) parts.push(product.mizajUrdu);
  if (product.mizaj) {
    parts.push(product.mizaj);
    const mizajObj = MIZAJ_LIST.find(m => m.id === product.mizaj);
    if (mizajObj) {
      parts.push(mizajObj.nameUrdu);
      parts.push(mizajObj.nameEn);
    }
  }

  // Category
  if (product.category) {
    parts.push(product.category);
    const catObj = CATEGORIES.find(c => c.id === product.category);
    if (catObj) {
      parts.push(catObj.nameUrdu);
      parts.push(catObj.nameEn);
    }
  }

  // Ailments / Diseases
  if (Array.isArray(product.ailments)) {
    product.ailments.forEach(ailId => {
      parts.push(ailId);
      const ailObj = AILMENTS_LIST.find(a => a.id === ailId);
      if (ailObj) {
        parts.push(ailObj.nameUrdu);
        parts.push(ailObj.nameEn);
      }
    });
  }

  return parts.join(' ');
};

/**
 * Match a product against a search query with scoring
 * Returns score > 0 if matched, 0 if not matched.
 */
export const scoreProductMatch = (product, query) => {
  if (!product || !query || !query.trim()) return 0;

  const rawQuery = query.toLowerCase().trim();
  const normQuery = normalizeSearchText(query);
  const phonQuery = phoneticNormalize(query);
  const queryTokens = normQuery.split(/\s+/).filter(Boolean);
  const phonQueryTokens = phonQuery.split(/\s+/).filter(Boolean);

  const rawNameUrdu = (product.nameUrdu || '').toLowerCase();
  const rawNameEn = (product.nameEn || '').toLowerCase();
  const normNameUrdu = normalizeSearchText(product.nameUrdu);
  const normNameEn = normalizeSearchText(product.nameEn);
  const phonNameUrdu = phoneticNormalize(product.nameUrdu);
  const phonNameEn = phoneticNormalize(product.nameEn);

  let score = 0;

  // 1. Exact or prefix match on Urdu or English title (Highest Priority)
  if (normNameUrdu === normQuery || rawNameUrdu === rawQuery) score += 150;
  if (phonNameUrdu === phonQuery) score += 130;
  if (normNameUrdu.startsWith(normQuery) || phonNameUrdu.startsWith(phonQuery)) score += 110;
  if (normNameUrdu.includes(normQuery) || phonNameUrdu.includes(phonQuery)) score += 90;

  if (normNameEn.startsWith(normQuery) || rawNameEn.startsWith(rawQuery)) score += 80;
  if (normNameEn.includes(normQuery) || rawNameEn.includes(rawQuery)) score += 70;

  // 2. Synonyms expansion check
  const expandedQueries = expandQuerySynonyms(query);
  for (const eq of expandedQueries) {
    if (
      normNameUrdu.includes(eq) || 
      normNameEn.includes(eq) || 
      rawNameUrdu.includes(eq) || 
      rawNameEn.includes(eq) ||
      phonNameUrdu.includes(eq)
    ) {
      score += 85;
      break;
    }
  }

  // 3. Multi-field corpus check
  const corpus = buildProductSearchCorpus(product);
  const normCorpus = normalizeSearchText(corpus);
  const phonCorpus = phoneticNormalize(corpus);

  if (normCorpus.includes(normQuery) || phonCorpus.includes(phonQuery)) {
    score += 40;
  }

  // Check expanded synonyms in corpus
  for (const eq of expandedQueries) {
    if (normCorpus.includes(eq) || phonCorpus.includes(eq)) {
      score += 35;
      break;
    }
  }

  // 4. Token-by-token matching (e.g. "موصلی سفید" or "خالص کلونجی")
  if (queryTokens.length > 1) {
    const allTokensMatch = queryTokens.every((tok, idx) => 
      normCorpus.includes(tok) || phonCorpus.includes(phonQueryTokens[idx])
    );
    if (allTokensMatch) {
      score += 60;
    } else {
      const anyTokenMatches = queryTokens.filter((tok, idx) => 
        normCorpus.includes(tok) || phonCorpus.includes(phonQueryTokens[idx])
      ).length;
      if (anyTokenMatches > 0) {
        score += anyTokenMatches * 15;
      }
    }
  }

  // 5. Fuzzy / Typo tolerance (Levenshtein distance)
  // Check individual words in product title against query words
  const titleTokens = normNameUrdu.split(/\s+/).filter(Boolean);
  const phonTitleTokens = phonNameUrdu.split(/\s+/).filter(Boolean);

  for (const qTok of queryTokens) {
    if (qTok.length < 3) continue;

    for (let i = 0; i < titleTokens.length; i++) {
      const tTok = titleTokens[i];
      const pTok = phonTitleTokens[i] || '';

      // Direct substring
      if (tTok.includes(qTok) || qTok.includes(tTok) || pTok.includes(qTok)) {
        score += 50;
        continue;
      }

      // Edit distance
      const maxAllowedDistance = qTok.length <= 4 ? 1 : 2;
      const distNorm = getEditDistance(qTok, tTok);
      const distPhon = pTok ? getEditDistance(phonQuery, pTok) : 99;

      if (distNorm <= maxAllowedDistance || distPhon <= maxAllowedDistance) {
        score += 45;
      }
    }
  }

  return score;
};

/**
 * Filter and sort products based on search query
 */
export const searchProducts = (products, query, limit = 8) => {
  if (!products || !Array.isArray(products)) return [];
  if (!query || !query.trim()) return [];

  const scored = [];
  for (const product of products) {
    const score = scoreProductMatch(product, query);
    if (score > 0) {
      scored.push({ product, score });
    }
  }

  // Sort descending by relevance score
  scored.sort((a, b) => b.score - a.score);

  const results = scored.map(item => item.product);
  return limit ? results.slice(0, limit) : results;
};
